import React from 'react';
import { Stack } from '@fluentui/react';
import { useStyles } from 'common/components/ActionMenu/Tags/index.styles';
import { EntityTagsSection } from 'common/components/ActionMenu/Tags';
import { EntityTagInput } from 'common/types/globalTypes';
import {
  SupplierAvailableTags,
  SupplierAvailableTagsVariables,
} from './__generated__/SupplierAvailableTags';
import {
  SupplierTags,
  SupplierTagsVariables,
} from './__generated__/SupplierTags';
import {
  EntityTagSupplierCreate,
  EntityTagSupplierCreateVariables,
} from './__generated__/EntityTagSupplierCreate';
import {
  EntityTagDelete,
  EntityTagDeleteVariables,
} from 'common/graphql/__generated__/EntityTagDelete';

import SUPPLIER_TAGS_LIST from './SupplierTags.graphql';
import AVAILABLE_TAGS_LIST from './SupplierAvailableTags.graphql';
import ADD_NEW_TAG from './EntityTagSupplierCreate.graphql';
import DELETE_TAG from 'common/graphql/EntityTagDelete.graphql';

interface TagsSectionProps {
  supplierID?: string;
}

export const TagsSection: React.FC<TagsSectionProps> = ({ supplierID }) => {
  const styles = useStyles();

  // Memoized functions for extracting data and generating variables
  const extractAvailableTags = React.useCallback(
    (data: SupplierAvailableTags | undefined) =>
      data?.supplierAvailableTags?.nodes.map((tag) => ({
        id: tag.id,
        badgeName: tag.badgeName,
        badgeColor: tag.badgeColor ?? undefined,
      })) ?? [],
    []
  );

  const extractEntityTags = React.useCallback(
    (data: SupplierTags | undefined) =>
      data?.supplier?.entityTagsByEntityId.nodes.map((tag) => ({
        id: tag.id,
        _rowTimestamp: tag._rowTimestamp!,
        _tagBadgeName: tag._tagBadgeName!,
        _tagBadgeColor: tag._tagBadgeColor ?? undefined,
        _createdDate: tag._createdDate!,
        createdByUser: tag.createdByUser ?? 'Unknown',
      })) ?? [],
    []
  );

  const getAvailableTagsVars = React.useCallback(
    (entityId: string): SupplierAvailableTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const getEntityTagsVars = React.useCallback(
    (entityId: string): SupplierTagsVariables => ({
      id: entityId,
    }),
    []
  );

  const createTagsVars = React.useCallback(
    (tags: EntityTagInput[]): EntityTagSupplierCreateVariables => ({
      input: {
        entityTag: tags,
      },
    }),
    []
  );

  const deleteTagsVars = React.useCallback(
    (id: string, rowTimestamp: string): EntityTagDeleteVariables => ({
      entityTagDeleteInput: {
        entityDelete: [{ id, rowTimestamp }],
      },
    }),
    []
  );

  if (!supplierID) {
    return null; // Early return if no supplierID
  }

  return (
    <Stack
      tokens={{ childrenGap: 15, padding: '0px 25px' }}
      className={styles.tagsContainer}
    >
      <EntityTagsSection<
        SupplierAvailableTags,
        SupplierAvailableTagsVariables,
        SupplierTags,
        SupplierTagsVariables,
        EntityTagSupplierCreate,
        EntityTagSupplierCreateVariables,
        EntityTagDelete,
        EntityTagDeleteVariables
      >
        entityId={supplierID}
        getAvailableTagsGql={AVAILABLE_TAGS_LIST}
        getEntityTagsGql={SUPPLIER_TAGS_LIST}
        addTagGql={ADD_NEW_TAG}
        deleteTagGql={DELETE_TAG}
        extractAvailableTags={extractAvailableTags}
        extractEntityTags={extractEntityTags}
        getAvailableTagsVars={getAvailableTagsVars}
        getEntityTagsVars={getEntityTagsVars}
        createTagsVars={createTagsVars}
        deleteTagsVars={deleteTagsVars}
      />
    </Stack>
  );
};
